import React from "react";

import {
  formatMoney,
  formatPercent,
} from "../../../utils/formatters";

export default function ScenarioFinancialPosition({
  scenario,
  financialPosition = {},
  settings,
  onReviewCoverage,
  onAdjustStrategy,
  onCompareAlternatives,
}) {
  if (!scenario) return null;

  const currency =
    settings?.currency || "USD";

  const grossP95 = Number(
    financialPosition?.gross_p95 ?? 0
  );

  const transferredP95 = Number(
    financialPosition?.transferred_p95 ?? 0
  );

  const capitalAtRisk = Number(
    financialPosition?.capital_at_risk ?? 0
  );

  const protectionRatio = normalizeRatio(
    financialPosition?.protection_ratio
  );

  const retainedRatio = Math.max(
    0,
    1 - protectionRatio
  );

  const assessment = buildAssessment({
    protectionRatio,
    capitalAtRisk,
    currency,
  });

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Scenario Financial Position
          </p>

          <h2 className="mt-2 text-xl font-semibold text-slate-900">
            {scenario.name}
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            {scenario.asset_name || "No asset assigned"}
            {scenario.incident_type
              ? ` · ${scenario.incident_type}`
              : ""}
          </p>
        </div>

        <ProtectionBadge
          ratio={protectionRatio}
        />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <MetricCard
          label="P95 Exposure"
          value={formatMoney(
            grossP95,
            currency
          )}
        />

        <MetricCard
          label="Transferred"
          value={formatMoney(
            transferredP95,
            currency
          )}
          tone="positive"
        />

        <MetricCard
          label="Capital at Risk"
          value={formatMoney(
            capitalAtRisk,
            currency
          )}
          tone={
            capitalAtRisk > 0
              ? "negative"
              : "positive"
          }
        />
      </div>

      <div className="mt-5">
        <div className="flex h-4 w-full overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full bg-emerald-500"
            style={{
              width: `${protectionRatio * 100}%`,
            }}
          />

          <div
            className="h-full bg-red-400"
            style={{
              width: `${retainedRatio * 100}%`,
            }}
          />
        </div>

        <div className="mt-2 flex justify-between text-xs text-slate-500">
          <span>
            Protected{" "}
            {formatPercent(
              protectionRatio,
              1
            )}
          </span>

          <span>
            Retained{" "}
            {formatPercent(
              retainedRatio,
              1
            )}
          </span>
        </div>
      </div>

      <div
        className={`mt-6 rounded-xl border p-4 ${assessment.style}`}
      >
        <p className="text-xs font-semibold uppercase tracking-wide">
          Executive Assessment
        </p>

        <h3 className="mt-2 text-base font-semibold">
          {assessment.title}
        </h3>

        <p className="mt-2 text-sm leading-6">
          {assessment.description}
        </p>

        <div className="mt-4 rounded-lg bg-white/60 px-4 py-3">
          <p className="text-xs font-semibold">
            Recommended Next Step
          </p>

          <p className="mt-1 text-sm font-medium">
            {assessment.recommendation}
          </p>
        </div>
      </div>

      <div className="mt-6 border-t border-slate-200 pt-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
            <h3 className="text-sm font-semibold text-slate-900">
                Decision Path
            </h3>

            <p className="mt-1 text-sm text-slate-500">
                Review the current position or evaluate alternative ways to finance the remaining exposure.
            </p>
            </div>

            <div className="flex flex-wrap gap-2">
            <DecisionButton
                onClick={onReviewCoverage}
            >
                Review Current Coverage
            </DecisionButton>

            <DecisionButton
                onClick={onAdjustStrategy}
            >
                Adjust Transfer Strategy
            </DecisionButton>

            <DecisionButton
                onClick={onCompareAlternatives}
                primary
            >
                Compare Alternatives
            </DecisionButton>
            </div>
        </div>
      </div>

    </section>
  );
}

function MetricCard({
  label,
  value,
  tone = "neutral",
}) {
  const valueClass = {
    neutral: "text-slate-900",
    positive: "text-emerald-700",
    negative: "text-red-700",
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-medium text-slate-500">
        {label}
      </p>

      <p
        className={`mt-2 text-xl font-semibold ${
          valueClass[tone]
        }`}
      >
        {value}
      </p>
    </div>
  );
}

function ProtectionBadge({
  ratio,
}) {
  const percentage =
    ratio * 100;

  const config =
    percentage >= 100
      ? {
          label: "Adequate",
          style:
            "bg-emerald-100 text-emerald-800",
        }
      : percentage >= 75
      ? {
          label: "Marginal",
          style:
            "bg-amber-100 text-amber-800",
        }
      : percentage > 0
      ? {
          label: "Underinsured",
          style:
            "bg-orange-100 text-orange-800",
        }
      : {
          label: "Uncovered",
          style:
            "bg-red-100 text-red-800",
        };

  return (
    <div
      className={`rounded-xl px-4 py-3 text-right ${config.style}`}
    >
      <p className="text-xs font-semibold">
        {config.label}
      </p>

      <p className="mt-1 text-2xl font-semibold">
        {formatPercent(
          ratio,
          1
        )}
      </p>
    </div>
  );
}

function buildAssessment({
  protectionRatio,
  capitalAtRisk,
  currency,
}) {
  if (protectionRatio >= 0.9) {
    return {
      title:
        "Current transfer arrangements provide strong financial protection.",
      description:
        `Only ${formatMoney(
          capitalAtRisk,
          currency
        )} of P95 exposure remains under direct organizational responsibility.`,
      recommendation:
        "Compare the cost of a modest limit increase with targeted mitigation investment before purchasing additional broad coverage.",
      style:
        "border-emerald-200 bg-emerald-50 text-emerald-900",
    };
  }

  if (protectionRatio >= 0.5) {
    return {
      title:
        "Current coverage reduces exposure, but material capital remains at risk.",
      description:
        `${formatMoney(
          capitalAtRisk,
          currency
        )} remains exposed after current transfer capacity.`,
      recommendation:
        "Evaluate additional transfer capacity, targeted controls and explicit risk retention as competing capital-allocation alternatives.",
      style:
        "border-amber-200 bg-amber-50 text-amber-900",
    };
  }

  return {
    title:
      "The scenario remains materially underprotected.",
    description:
      `${formatMoney(
        capitalAtRisk,
        currency
      )} remains at risk under the current financing position.`,
    recommendation:
      "Prioritize this scenario for detailed decision optimization across mitigation, transfer and retention alternatives.",
    style:
      "border-red-200 bg-red-50 text-red-900",
  };
}

function normalizeRatio(value) {
  const number = Number(value ?? 0);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.min(
    Math.max(number, 0),
    1
  );
}

function DecisionButton({
  children,
  onClick,
  primary = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={`rounded-lg px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-50 ${
        primary
          ? "bg-slate-900 text-white hover:bg-slate-800"
          : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}